import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { updateAlarmsFilter } from "rtkSlices/AlarmsFilterSlice";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Button } from "@mui/material";
import Card from "@mui/material/Card";
import InputBase from "@mui/material/InputBase";
import Popover from "@mui/material/Popover";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined';
import { AdminSelect } from "components/Admin/adminTable";

const DAY = 1000 * 60 * 60 * 24;
const showOnlyOptions = [
  { value: "emails", label: "Sent e-mails" },
  { value: "actuations", label: "Ran actuations" },
];
const searchByOptions = [
  { label: "Asset", value: "asset" },
  { label: "Rule", value: "rule" },
];

const validDate = (d) => d instanceof Date && !isNaN(d);

// A several-choice filter that applies when its menu closes, so ticking three
// priorities sends one request. The picks keep the options' order.
function MultiFilter({ value, onApply, options, ...props }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [JSON.stringify(value)]);
  return (
    <AdminSelect
      {...props}
      multiple
      options={options}
      value={draft}
      onChange={setDraft}
      onClose={() => {
        const picked = options
          .map((o) => o.value)
          .filter((v) => draft.includes(v));
        if (JSON.stringify(picked) !== JSON.stringify(value)) onApply(picked);
      }}
    />
  );
}

// The filter row at the top of the Alarms Dashboard. Each control writes only
// its own key of the filter (updateAlarmsFilter).
export default function AlarmFilters({
  priorities,
  statuses,
  solutions,
  filters,
  serviceId,
  origin,
  view,
}) {
  // useHistory() over a `history` prop — the dashboard is embedded in ESB
  // AggregatorCCDashboard and Energy Management without `history` passed,
  // which made the Back button crash on click in those modes. Reading from
  // Router context works in every mount path.
  const history = useHistory();
  const dispatch = useDispatch();
  const backUrl = origin?.backUrl ?? "/solutions/";
  const [start, setStart] = useState(new Date(filters.date.startTime));
  const [end, setEnd] = useState(new Date(filters.date.endTime));
  const [dateError, setDateError] = useState("");
  const [searchText, setSearchText] = useState(filters.search.text);
  const [anchorEl, setAnchorEl] = useState(null);
  const [byDraft, setByDraft] = useState([]);
  const searchTimer = useRef(null);
  // the latest search key, read when the timer fires (a "Search by" change can
  // land while it waits)
  const searchRef = useRef(filters.search);
  searchRef.current = filters.search;

  useEffect(() => {
    return () => clearTimeout(searchTimer.current);
  }, []);

  // The pickers follow the store's range (the page sets a fresh 7 days when it
  // opens) (code review 2026-10-30).
  useEffect(() => {
    setStart(new Date(filters.date.startTime));
    setEnd(new Date(filters.date.endTime));
    setDateError("");
  }, [filters.date.startTime, filters.date.endTime]);

  // The search applies when typing pauses, as on Device Management.
  const handleSearch = (text) => {
    setSearchText(text);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (text.trim() !== searchRef.current.text) {
        dispatch(
          updateAlarmsFilter({
            search: { ...searchRef.current, text: text.trim() },
          })
        );
      }
    }, 1000);
  };

  // The "Search by" picker applies when it closes (the last ticked field
  // cannot be unticked); the fields keep the picker's order.
  const openPicker = (e) => {
    setByDraft(filters.search.by);
    setAnchorEl(e.currentTarget);
  };
  const closePicker = () => {
    setAnchorEl(null);
    const picked = searchByOptions
      .map((f) => f.value)
      .filter((v) => byDraft.includes(v));
    if (
      picked.length &&
      JSON.stringify(picked) !== JSON.stringify(searchRef.current.by)
    ) {
      dispatch(
        updateAlarmsFilter({ search: { ...searchRef.current, by: picked } })
      );
    }
  };

  // A date applies on accept (the pop-up) or on blur or Enter (typed), only
  // when both dates are valid, the start is before the end and the range is at
  // most 60 days, all checked on the new values. Otherwise the message shows
  // and the last good range stays.
  const applyDates = (from, to) => {
    if (!validDate(from) || !validDate(to)) {
      setDateError("Enter a complete start and end time.");
    } else if (from >= to) {
      setDateError("The start time must be before the end time.");
    } else if (to - from > 60 * DAY) {
      setDateError("Please select a range of at most 60 days.");
    } else {
      setDateError("");
      if (
        from.getTime() != new Date(filters.date.startTime).getTime() ||
        to.getTime() != new Date(filters.date.endTime).getTime()
      ) {
        dispatch(
          updateAlarmsFilter({
            date: {
              startTime: from.getTime(),
              endTime: to.getTime(),
              endPicked:
                !!filters.date.endPicked ||
                to.getTime() != new Date(filters.date.endTime).getTime(),
            },
          })
        );
      }
    }
  };

  const datePicker = (label, value, setValue, accept) => (
    <DateTimePicker
      label={label}
      value={value}
      format="dd/MM/yyyy hh:mm aa"
      onChange={setValue}
      onAccept={accept}
      slotProps={{
        textField: {
          size: "small",
          sx: { width: "230px" },
          // undefined keeps the picker's own check for a half-typed date
          error: dateError ? true : undefined,
          onBlur: () => applyDates(start, end),
          onKeyDown: (e) => {
            if (e.key === "Enter") applyDates(start, end);
          },
        },
      }}
    />
  );

  return (
    <Card style={{ padding: "12px 16px" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!serviceId ? (
          <MultiFilter
            id="alarms-solution-filter"
            label="Solution"
            width="200px"
            allLabel="All solutions"
            value={filters.solutions}
            onApply={(picked) =>
              dispatch(updateAlarmsFilter({ solutions: picked }))
            }
            options={solutions.map((s) => ({ value: s.name, label: s.name }))}
            renderValue={(picked) =>
              !picked.length
                ? "All solutions"
                : picked.length == 1
                ? picked[0]
                : `${picked.length} solutions`
            }
          />
        ) : null}
        <MultiFilter
          id="alarms-priority-filter"
          label="Priority"
          width="180px"
          allLabel="All priorities"
          value={filters.priority}
          onApply={(picked) => dispatch(updateAlarmsFilter({ priority: picked }))}
          options={priorities.map((p) => ({ value: p, label: p }))}
          renderValue={(picked) =>
            picked.length ? picked.join(", ") : "All priorities"
          }
        />
        {/* the alarm log has no status, e-mail or actuation: hidden in Timeline view */}
        {view != "timeline" ? (
          <>
            <MultiFilter
              id="alarms-status-filter"
              label="Status"
              width="180px"
              allLabel="All statuses"
              value={filters.status}
              onApply={(picked) => dispatch(updateAlarmsFilter({ status: picked }))}
              options={statuses.map((s) => ({ value: s, label: s }))}
              renderValue={(picked) =>
                picked.length ? picked.join(", ") : "All statuses"
              }
            />
            <MultiFilter
              id="alarms-show-only-filter"
              label="Show only"
              width="180px"
              allLabel="All alarms"
              value={showOnlyOptions
                .map((o) => o.value)
                .filter((k) => filters[k])}
              onApply={(picked) =>
                dispatch(
                  updateAlarmsFilter({
                    emails: picked.includes("emails"),
                    actuations: picked.includes("actuations"),
                  })
                )
              }
              options={showOnlyOptions}
              renderValue={(picked) =>
                picked.length
                  ? showOnlyOptions
                      .filter((o) => picked.includes(o.value))
                      .map((o) => o.label)
                      .join(", ")
                  : "All alarms"
              }
            />
          </>
        ) : null}
        <div>
          <div style={{ display: "flex", gap: "10px" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              {datePicker("Start Time", start, setStart, (v) =>
                applyDates(v, end)
              )}
              {datePicker("End Time", end, setEnd, (v) =>
                applyDates(start, v)
              )}
            </LocalizationProvider>
          </div>
          {dateError ? (
            <span
              style={{
                display: "block",
                color: "red",
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {dateError}
            </span>
          ) : null}
        </div>
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          <InputBase
            id="alarms-search"
            placeholder="Search asset or rule ..."
            sx={{
              border: "1px solid rgb(215, 215, 215)",
              borderRadius: "10px",
              padding: "12px",
              paddingRight: "40px",
              width: "300px",
              height: "40px",
            }}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            inputProps={{ "aria-label": "Search" }}
          />
          <TuneIcon
            id="alarms-search-fields"
            sx={{ marginLeft: "-35px", cursor: "pointer", color: "#222" }}
            onClick={openPicker}
          />
          <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={closePicker}>
            <div style={{ padding: 20, paddingRight: 2 }}>
              <span style={{ fontSize: "13px", color: "rgb(180, 180, 180)", marginBottom: 5 }}>
                Search by
              </span>
              {searchByOptions.map((f) => (
                <FormGroup key={f.value}>
                  <FormControlLabel
                    label={f.label}
                    control={
                      <Checkbox
                        checked={byDraft.includes(f.value)}
                        disabled={byDraft.length === 1 && byDraft.includes(f.value)}
                        onChange={(e) =>
                          setByDraft((d) =>
                            e.target.checked
                              ? [...d, f.value]
                              : d.filter((v) => v !== f.value)
                          )
                        }
                      />
                    }
                  />
                </FormGroup>
              ))}
            </div>
          </Popover>
        </div>
        <Tooltip title="Back to Solution" placement="left">
          <Button
            variant="outlined"
            onClick={() => history.push(backUrl)}
            sx={{ borderColor: "#DBDBDB", height: "40px", marginLeft: "auto" }}
          ><KeyboardReturnOutlinedIcon/></Button>
        </Tooltip>
      </div>
    </Card>
  );
}
